import { useState, useRef, useEffect, useCallback } from 'react';

interface DropdownPos {
  readonly top: number;
  readonly left: number;
  readonly width: number;
}

interface UseDropdownPortalReturn<T extends HTMLElement> {
  readonly isOpen: boolean;
  readonly isMounted: boolean;
  readonly dropdownDataState: 'open' | 'closed';
  readonly searchTerm: string;
  readonly setSearchTerm: (term: string) => void;
  readonly dropdownPos: DropdownPos;
  readonly containerRef: React.RefObject<HTMLDivElement>;
  readonly triggerRef: React.RefObject<T>;
  readonly dropdownRef: React.RefObject<HTMLDivElement>;
  readonly handleOpen: () => void;
  readonly handleAnimationEnd: (e: React.AnimationEvent<HTMLDivElement>) => void;
  readonly close: () => void;
}

export function useDropdownPortal<T extends HTMLElement = HTMLElement>(
  disabled: boolean = false
): UseDropdownPortalReturn<T> {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPos, setDropdownPos] = useState<DropdownPos>({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<T>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closingGuard = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finishClosing = useCallback(() => {
    if (!closingGuard.current) return;
    closingGuard.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(false);
    setIsClosing(false);
    setSearchTerm('');
  }, []);

  const close = useCallback(() => {
    if (!isOpen) return;
    closingGuard.current = true;
    setIsClosing(true);
    timeoutRef.current = setTimeout(finishClosing, 200);
  }, [isOpen, finishClosing]);

  const handleAnimationEnd = useCallback((e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'exit' && e.currentTarget === e.target) {
      finishClosing();
    }
  }, [finishClosing]);

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);

  // Click-outside: check both the trigger container and the portal dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [close]);

  // Close dropdown on scroll (from any ancestor, but not from the dropdown itself)
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) return;
      close();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, close]);

  const handleOpen = () => {
    if (disabled) return;
    if (isOpen) {
      close();
      return;
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    closingGuard.current = false;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownMaxH = 280;
      const top = spaceBelow < 200 && rect.top > spaceBelow
        ? Math.max(8, rect.top - dropdownMaxH - 8)
        : rect.bottom + 8;
      setDropdownPos({ top, left: rect.left, width: rect.width });
    }
    setIsClosing(false);
    setIsOpen(true);
  };

  return {
    isOpen,
    isMounted: isOpen || isClosing,
    dropdownDataState: isClosing ? 'closed' : 'open',
    searchTerm,
    setSearchTerm,
    dropdownPos,
    containerRef,
    triggerRef,
    dropdownRef,
    handleOpen,
    handleAnimationEnd,
    close,
  };
}
